import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import api from '../../../lib/api';

const FeaturedProjects = () => {
  const [activeProject, setActiveProject] = useState(0);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Default projects as fallback
  const defaultProjects = [
    {
      id: 1,
      title: 'PoidhAI',
      category: 'Web3 Development',
      description: 'A sophisticated DeFi protocol that automatically optimizes yield farming strategies across multiple blockchains, featuring smart contract automation and risk management.',
      image: '/projects/poidhai.png',
      technologies: ['Next.js', 'Farcaster Frame', "AI", 'reown', 'poidh protocol', 'AI', 'Vercel', 'Tailwind CSS', 'Shadcn UI'],
      stats: {
        visitors: '100+',
        accuracy: '90%',
        BountiesCreated: '10+'
      },
      links: {
        demo: 'https://poidhai-omega.vercel.app/',
        github: 'https://github.com/navdiya-nikunj/poidhai',
        live: 'https://poidhai-omega.vercel.app/'
      },
      world: 'web3'
    },
    {
      id: 3,
      title: 'StackChemi',
      category: 'Software Development',
      description: 'StackChemi is a modern, user-friendly web application that helps users analyze potential interactions between multiple medications. Built with Next.js and powered by AI, it provides real-time visualization of drug interactions and detailed safety information.',
      image: '/projects/stackchemi.png',
      technologies: ['Next.js', 'AI', 'Vercel', 'D3.js', 'Tailwind CSS', 'Shadcn UI'],
      stats: {
        visitors: '100+',
        accuracy: '90%',
        efficiency: '+80%'
      },
      links: {
        demo: 'https://youtu.be/cPXqB_RIqRA',
        github: 'https://github.com/navdiya-nikunj/stackchemi',
        live: 'https://stackchemi.vercel.app/'
      },
      world: 'software'
    },
    {
      id: 4,
      title: 'CertifyMe',
      category: 'Web3 Development',
      description: 'CertifyMe is a revolutionary blockchain-based certification platform that leverages Web3 technology to issue, manage, and verify digital certificates as NFTs on the Ethereum blockchain. ',
      image: '/projects/certifyme.png',
      technologies: ['Solidity', 'IPFS', 'React', 'Ethereum'],
      stats: {
        issued: '100+',
        uptime: '99.99%',
        users: '100+'
      },
      links: {
        demo: 'https://certifyme.club',
        github: 'https://github.com/navdiya-nikunj/CertifyMe',
        live: 'https://certifyme.club'
      },
      world: 'web3'
    }
  ];

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await api.projects.getAll({ featured: true });
        if (response.projects && response.projects.length > 0) {
          setProjects(response.projects);
        } else {
          // Fallback to default projects if none returned
          setProjects(defaultProjects);
        }
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError('Failed to load projects');
        setProjects(defaultProjects);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, []);

  const getWorldColor = (world) => {
    switch (world) {
      case 'web3': return 'warning';
      case 'community': return 'success';
      case 'software': return 'primary';
      default: return 'primary';
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col items-center">
            <Icon name="Loader" size={40} className="text-primary animate-spin mb-4" />
            <p className="font-body text-text-secondary">Loading projects...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
            Featured{' '}
            <span className="bg-gradient-to-r from-accent to-warning bg-clip-text text-transparent">
              Projects
            </span>
          </h2>
          <p className="font-body text-text-secondary text-lg max-w-3xl mx-auto">
            A showcase of my most impactful work across all three worlds. Each project represents innovation, technical excellence, and real-world impact.
          </p>
        </motion.div>

        {/* Projects Carousel */}
        <div className="relative">
          {/* Main Project Display */}
          <motion.div
            key={activeProject}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12"
          >
            {/* Project Image */}
            <div className="relative group">
              <div className="relative overflow-hidden rounded-2xl shadow-elevation-3 group-hover:shadow-elevation-3 transition-all duration-300">
                <Image
                  src={projects[activeProject].image}
                  alt={projects[activeProject].title}
                  className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Overlay Links */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="flex space-x-4">
                    {Object.entries(projects[activeProject].links).map(([type, link]) => (
                      <button
                        key={type}
                        className="w-12 h-12 bg-surface rounded-full flex items-center justify-center hover:bg-primary hover:text-surface transition-colors duration-200"
                        title={type.charAt(0).toUpperCase() + type.slice(1)}
                        onClick={()=>{
                          window.open(link, '_blank');
                        }}
                      >
                        <Icon 
                          name={type === 'demo' ? 'Play' : type === 'github' ? 'Github' : 'ExternalLink'} 
                          size={20} 
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <span className={`px-3 py-1 bg-${getWorldColor(projects[activeProject].world)}/10 text-${getWorldColor(projects[activeProject].world)} rounded-full font-caption text-sm font-medium`}>
                  {projects[activeProject].category}
                </span>
                <div className={`w-2 h-2 bg-${getWorldColor(projects[activeProject].world)} rounded-full animate-pulse`}></div>
              </div>

              <h3 className="font-heading text-3xl font-bold text-primary mb-4">
                {projects[activeProject].title}
              </h3>

              <p className="font-body text-text-secondary leading-relaxed mb-6">
                {projects[activeProject].description}
              </p>

              {/* Technologies */}
              <div className="mb-6">
                <h4 className="font-body font-semibold text-primary mb-3">Technologies Used</h4>
                <div className="flex flex-wrap gap-2">
                  {projects[activeProject].technologies.map((tech, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-surface border border-border rounded-lg font-caption text-sm text-text-secondary"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                {Object.entries(projects[activeProject].stats).map(([key, value], index) => (
                  <div key={index} className="text-center">
                    <div className={`font-heading text-2xl font-bold text-${getWorldColor(projects[activeProject].world)}`}>
                      {value}
                    </div>
                    <div className="font-caption text-text-secondary text-xs capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={()=>{
                  window.open(projects[activeProject].links.live, '_blank')
                }} className={`bg-${getWorldColor(projects[activeProject].world)} text-surface px-6 py-3 rounded-lg font-body font-medium hover:opacity-90 transition-opacity duration-200 flex items-center justify-center space-x-2`}>
                  <Icon name="ExternalLink" size={18} />
                  <span>View Live Project</span>
                </button>
                <button onClick={()=>{
                  window.open(projects[activeProject].links.github, '_blank')
                }} className={`border border-${getWorldColor(projects[activeProject].world)} text-${getWorldColor(projects[activeProject].world)} px-6 py-3 rounded-lg font-body font-medium hover:bg-${getWorldColor(projects[activeProject].world)} hover:text-surface transition-colors duration-200 flex items-center justify-center space-x-2`}>
                  <Icon name="Github" size={18} />
                  <span>View Code</span>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Project Navigation */}
          <div className="flex justify-center space-x-4 mb-8">
            {projects.map((project, index) => (
              <button
                key={project.id}
                onClick={() => setActiveProject(index)}
                className={`w-16 h-16 rounded-xl border-2 overflow-hidden transition-all duration-200 ${
                  index === activeProject
                    ? `border-${getWorldColor(project.world)} shadow-elevation-2`
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <Image
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>

          {/* Navigation Arrows */}
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setActiveProject((prev) => (prev - 1 + projects.length) % projects.length)}
              className="w-12 h-12 bg-surface border border-border rounded-full flex items-center justify-center hover:bg-primary hover:text-surface hover:border-primary transition-colors duration-200"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>
            <button
              onClick={() => setActiveProject((prev) => (prev + 1) % projects.length)}
              className="w-12 h-12 bg-surface border border-border rounded-full flex items-center justify-center hover:bg-primary hover:text-surface hover:border-primary transition-colors duration-200"
            >
              <Icon name="ChevronRight" size={20} />
            </button>
          </div>
        </div>

        {/* View All Projects CTA */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="font-body text-text-secondary mb-6">
            Want to see more? Explore my complete project portfolio in each world.
          </p>
          <button className="bg-accent text-surface px-8 py-4 rounded-lg font-body font-medium hover:bg-accent/90 transition-all duration-200 transform hover:scale-105 shadow-elevation-2">
            View All Projects
          </button>
        </motion.div> */}
      </div>
    </section>
  );
};

export default FeaturedProjects;